import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormCaption, InformSpan, SixDigitInput } from '../../common/components';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { APP_KEYS } from '../../common/consts';
import { authService } from '../service/auth.service';
import { IActivateAccount } from '../types';
import { useThrottle } from '../../hooks/useThrottle';
import useAuth from '../feature/auth';

interface Inputs {
  code: string;
}

export const ActivateAccount = () => {
  const { t } = useTranslation();
  const [serverError, setServerError] = React.useState('');
  const [formattedValue, setFormattedValue] = React.useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated, checkAuth } = useAuth();
  const { email } = (location.state as { email?: string }) ?? {};

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<Inputs>();

  const { mutate: activate } = useMutation({
    mutationFn: (data: IActivateAccount) => authService.activateAccount(data),
    onSuccess: async () => {
      setIsAuthenticated(true);
      await checkAuth();
      navigate(ROUTER_LINKS.ROOT);
    },
    onError: (err: any) => {
      if (err.response?.data?.message === 'Invalid token') {
        setServerError(t('auth.codeIncorrect'));
      }
    }
  });
  const throttledActivate = useThrottle(activate);

  const { mutate: resendCode } = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.RECOVERY_CODE],
    mutationFn: (emailAddr: string) =>
      authService.resendActivationCode({ email: emailAddr }),
    onSuccess: () => {
      setServerError('');
    },
    onError: () => {
      setServerError(t('auth.resendFailed'));
    }
  });
  const throttledResendCode = useThrottle(resendCode);

  const onSubmit = (data: Inputs) => {
    const code = data.code.replace(/\s/g, '');
    if (code.length === 6) {
      throttledActivate({ code });
    }
  };

  React.useEffect(() => {
    const code = formattedValue.replace(/\s/g, '');
    setValue('code', formattedValue);
    if (code.length === 6) {
      throttledActivate({ code });
    }
  }, [formattedValue]);

  return (
    <div className={`relative ${serverError ? 'pt-[22px] md:pt-[32px]' : ''}`}>
      {serverError && (
        <div className="absolute flex justify-center gap-[8px] items-center top-[-22px] md:top-[-32px] left-[-22px] md:left-[-32px] w-[345px] md:w-[456px] h-[32px] bg-[#E42C2C] text-white text-center rounded-t-[42px] py-[6px] font-body text-[14px] font-regular">
          {serverError}
        </div>
      )}
      <FormCaption>{t('auth.activateTitle')}</FormCaption>

      <InformSpan>
        {email ? t('auth.activateSubtitle', { email }) : t('auth.activateSubtitleFallback')}
      </InformSpan>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <SixDigitInput
          errors={errors}
          name="code"
          setFormattedValue={setFormattedValue}
          formattedValue={formattedValue}
          register={register}
        >
          <div className="flex gap-[10px] justify-center text-center mt-[24px]">
            <p className="text-[16px] font-regular font-body text-gray-900">
              {t('auth.dontSeeCode')}
            </p>
            <div
              className="cursor-pointer"
              onClick={() => email && throttledResendCode(email)}
            >
              <span className="text-[16px] font-medium font-body text-base-black">
                {t('auth.resendCode')}
              </span>
            </div>
          </div>
        </SixDigitInput>
      </form>
    </div>
  );
};
